<template>
  <footer class="footer bg__color--vdw">
    <div class="container footer__container">
      <div class="footer__col">
        <app-logo
          :logo="column1.logo.img"
          :url="column1.logo.url"
          class="logo"
        />

        <ul
          class="nav"
          v-if="column1.links?.length"
        >
          <li
            class="nav__li"
            v-for="({ link }, key) in column1.links"
            :key="`link_${key}`"
          >
            <app-link
              :link="link"
              class="nav__a link__color--prim link--naked"
              target="_self"
            />
          </li>
        </ul>

        <ul
          class="networks"
          v-if="socialNetworks.length"
        >
          <li
            class="networks__li"
            v-for="(network, i) in socialNetworks"
            :key="'footer_network_' + i"
          >
            <a
              class="networks__a"
              :href="translate(network.url)"
              :aria-label="translate(network.text)"
              target="_blank"
            >
              <fa-icon
                class="networks__icon"
                size="lg"
                :icon="network.icon"
              />
            </a>
          </li>
        </ul>
      </div>

      <div class="footer__col">
        <h4
          class="footer__title align--center"
          v-if="translate(column2.title)"
        >
          {{ translate(column2.title) }}
        </h4>

        <a
          class="logo__wrapper"
          :href="translate(column2.logo.url)"
          v-if="translate(column2.logo.url)"
        >
          <img
            class="logo"
            :src="translate(column2.logo.img)"
          >
        </a>

        <ul
          class="networks"
          v-if="column2.social_networks.length"
        >
          <template
            v-for="({ network }, i) in column2.social_networks"
            :key="'footer_organization_network_' + i"
          >
            <li
              class="networks__li"
              v-if="translate(network.url)"
            >
              <a
                class="networks__a"
                :href="translate(network.url)"
                target="_blank"
              >
                <fa-icon
                  class="networks__icon"
                  size="lg"
                  :icon="['fab', network.icon]"
                />
              </a>
            </li>
          </template>
        </ul>
      </div>

      <div class="footer__col footer__col--contact">
        <h4
          class="footer__title"
          v-if="translate(column3.title)"
        >
          {{ translate(column3.title) }}
        </h4>

        <div class="infos infos--address">
          <fa-icon
            class="infos__icon"
            :icon="['fal', 'map-marker-alt']"
          />

          <div>
            <address
              v-html="address"
              v-if="address"
            />

            <a
              class="link__color--prim link--naked"
              :href="`tel:${phone.replace(/[^\d.-]+/g, '')}`"
              v-if="phone"
            >
              {{ phone }}
            </a>
          </div>
        </div>

        <div class="infos">
          <fa-icon
            class="infos__icon"
            :icon="['fal', 'globe']"
          />

          <app-link
            class="link__color--prim link--naked infos__website"
            target="_self"
            :link="column3.website"
          />
        </div>
      </div>
    </div>

    <nav
      class="container legal-nav"
      v-if="column4.links?.length"
    >
      <ul class="legal-nav__ul">
        <li
          class="legal-nav__li"
          v-for="({ link }, key) in column4.links"
          :key="`legal-link_${key}`"
        >
          <app-link
            :link="link"
            class="legal-nav__a link__color--prim link--naked"
            target="_self"
          />
        </li>
      </ul>
    </nav>
  </footer>
</template>

<script setup>
import { computed } from 'vue'
import { useClientStore } from '@/stores/ClientStore'
import { useI18n } from '@/vendors/i18n'
import { get as _get } from 'lodash'

import AppLogo from '&/atoms/AppLogo'
import AppLink from '&/atoms/AppLink'

const props = defineProps({ section: { type: Object, required: true } })

const { getClient } = useClientStore()
const { translate } = useI18n()

const { client } = getClient()

const column1 = computed(() => _get(props, 'section.attributes.column_1', {}))
const column2 = computed(() => _get(props, 'section.attributes.column_2', {}))
const column3 = computed(() => _get(props, 'section.attributes.column_3', {}))
const column4 = computed(() => _get(props, 'section.attributes.column_4', {}))

const socialNetworks = computed(() => _get(client.value, 'platform.social_networks', []).filter(({ url }) => translate(url)))
const address = computed(() => column3.value?.address?.map(({ text }) => translate(text)).join('<br />'))
const phone = computed(() => translate(column3.value?.phone))
</script>

<style lang="scss" scoped>
$font-size__base: 18px;

.container {
  max-width: 1430px;
  padding-left: 20px;
  padding-right: 20px;

  @include mq(md) {
    padding-left: 30px;
    padding-right: 30px;
  }
}

.footer {
  font-weight: 400;
  padding: 60px 0;

  &__container {
    display: grid;
    grid-template-columns: 1fr;

    @include mq(md) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__col {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 0;

    &:first-child {
      padding-top: 0;
    }

    @include mq(md) {
      padding: 0
    }

    &--contact {
      @include mq(md) {
        padding-left: 40px;
        align-items: flex-start;
      }
    }

    &:not(:last-child) {
      border-bottom: 1px solid $color--primary;

      @include mq(md) {
        border-bottom: 0;
        border-right: 1px solid $color--primary;
      }
    }
  }

  &__title {
    font-size: rem(25px);
    margin-bottom: 20px;
    color: $color--primary;
    font-weight: 500;
  }
}

.infos {
  display: flex;
  align-items: flex-start;
  color: $color--primary;
  gap: 6px;
  margin-bottom: 20px;

  &--address {
    font-size: rem(14px);
  }

  &__icon {
    font-size: 24px;
    min-width: 24px;
  }

  &__website {
    font-size: rem(18px);
  }
}

:deep() {
  .logo {
    width: auto;
    height: 100px;
  }
}

.nav {
  margin-top: 20px;
  font-size: rem(18px);
  line-height: 1;

  &__li {
    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }
}

.networks {
  display: flex;
  gap: 10px;
  margin-top: 20px;

  &__a {
    width: 30px;
    height: 30px;
    background: $color--primary;
    color: $white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    transition: all $transition__duration;

    &:hover {
      background: $color--tertiary;
      color: $color--primary;
    }
  }
}

.legal-nav {
  &__ul {
    font-size: rem(18px);
    text-align: center;
    padding-top: 30px;
    border-top: 1px solid $color--primary;

    @include mq(md) {
      border: 0;
      padding-top: 80px;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      justify-content: center;
      gap: 20px;
    }
  }

  &__li {
    padding: 10px 0;

    @include mq(md) {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 0;
    }

    &:not(:last-child) {
      &::after {
        content: '';
        display: block;
        width: 20px;
        height: 1px;
        background: $color--primary;
        margin: 20px auto 0;

        @include mq(md) {
          margin: 0;
        }
      }
    }
  }

  :deep(.nav__a) {
    display: block;
  }
}
</style>